import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import type { ReactNode } from "react";
import {
  CONTAINER_DEADLINE_RULES,
  deadlineEntries,
  deadlineStatus,
  daysUntilDate,
  ITEM_DEADLINE_RULES,
  matchesDeadlineFilters,
  worstDeadlineStatus,
  type DeadlineFilter,
  type DeadlineRuleSet,
  type DeadlineStatus,
  type Properties,
} from "@egalregal/shared";
import { useT, type MessageKey, type Translate } from "../i18n";
import { useAppStore } from "../store/app";
import { ChevronDownIcon, ChevronUpIcon } from "./icons";

/**
 * Deadline surfaces shared by item and container details and lists (deadlines
 * 02/03/04, prototype verdict cell D2 + opt-in column F). Pure derivation lives
 * in shared `properties.ts`; these components only paint it. Every surface takes
 * a `DeadlineVocabulary`, so containers reuse the same cells instead of a copy.
 */

const LABEL_KEYS: Record<string, MessageKey> = {
  bestBefore: "deadlineBestBefore",
  returnBy: "deadlineReturnBy",
  expiresOn: "deadlineExpiresOn",
  warrantyUntil: "deadlineWarrantyUntil",
  purchasedOn: "deadlinePurchasedOn",
};

/** Which property keys behave as deadlines and how early each warns. */
export type DeadlineVocabulary = DeadlineRuleSet;

/** Items: four deadlines, warranty warning a month ahead (ADR-0037). */
export const ITEM_DEADLINE_VOCABULARY: DeadlineVocabulary = ITEM_DEADLINE_RULES;

/** Containers carry expires-on only and warn four weeks ahead (ADR-0037). */
export const CONTAINER_DEADLINE_VOCABULARY: DeadlineVocabulary = CONTAINER_DEADLINE_RULES;

/** Localised property name for a date key; unknown keys fall back to the key. */
export function datePropertyLabel(t: Translate, key: string): string {
  const messageKey = LABEL_KEYS[key];
  return messageKey ? t(messageKey) : key;
}

/** Set deadlines of one entity, earliest first, for the given vocabulary. */
function deadlinesOf(properties: Properties | undefined, vocabulary: DeadlineVocabulary) {
  return deadlineEntries(properties, vocabulary.keys);
}

/** Warning state of one deadline under its vocabulary's threshold. */
function statusOf(
  key: string,
  date: string,
  vocabulary: DeadlineVocabulary,
  now: Date,
): DeadlineStatus {
  return deadlineStatus(date, vocabulary.thresholdDays(key), now);
}

/** MUI colour for one status: red past, amber inside the window, neutral else. */
export function deadlineColor(status: DeadlineStatus): "error" | "warning" | "default" {
  if (status === "overdue") return "error";
  if (status === "soon") return "warning";
  return "default";
}

/** Raw palette token for dots/rails (chips use `deadlineColor`). */
function deadlineToken(status: DeadlineStatus): string {
  if (status === "overdue") return "error.main";
  if (status === "soon") return "warning.main";
  return "text.disabled";
}

/** `12 Nov 2026` — day precision, no timezone shift. */
export function formatDeadlineDate(iso: string, language: "en" | "de"): string {
  const [year, month, day] = iso.split("-").map(Number) as [number, number, number];
  return new Date(year, month - 1, day).toLocaleDateString(language === "de" ? "de-DE" : "en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Compact countdown label: `3d`, localised `today` / `yesterday`, `−2d`. */
export function countdownLabel(iso: string, t: Translate, now: Date = new Date()): string {
  const days = daysUntilDate(iso, now);
  if (days === 0) return t("deadlineToday");
  if (days === -1) return t("deadlineYesterday");
  return days > 0 ? `${days}d` : `−${-days}d`;
}

/** Urgent-row dot for the hidden-column view; nothing when the row is calm. */
export function DeadlineStatusDot({
  properties,
  now = new Date(),
  vocabulary = ITEM_DEADLINE_VOCABULARY,
}: {
  properties?: Properties;
  now?: Date;
  vocabulary?: DeadlineVocabulary;
}) {
  const t = useT();
  const status = worstDeadlineStatus(properties, vocabulary, now);
  if (!status || status === "later") return null;
  return (
    <Box
      component="span"
      role="img"
      aria-label={t(status === "overdue" ? "deadlineOverdue" : "deadlineSoon")}
      data-testid="deadline-status-dot"
      sx={{
        width: 8,
        height: 8,
        flexShrink: 0,
        borderRadius: "50%",
        bgcolor: status === "overdue" ? "error.main" : "warning.main",
      }}
    />
  );
}

/**
 * Detail-page chips for one deadline vocabulary: one painted chip per set
 * deadline. `children` appends further chips (the item's reference date).
 */
export function DeadlineChips({
  properties,
  now = new Date(),
  vocabulary = ITEM_DEADLINE_VOCABULARY,
  children,
}: {
  properties?: Properties;
  now?: Date;
  vocabulary?: DeadlineVocabulary;
  children?: ReactNode;
}) {
  const t = useT();
  const language = useAppStore((s) => s.language);
  const dates = deadlinesOf(properties, vocabulary);
  if (dates.length === 0 && !children) return null;
  return (
    <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: "wrap" }}>
      {dates.map(({ key, date }) => (
        <Chip
          key={key}
          size="small"
          variant="outlined"
          color={deadlineColor(statusOf(key, date, vocabulary, now))}
          label={`${datePropertyLabel(t, key)} · ${formatDeadlineDate(date, language)}`}
        />
      ))}
      {children}
    </Stack>
  );
}

/** Item detail chips: deadlines plus the neutral purchased-on reference date. */
export function DatePropertyChips({
  properties,
  now = new Date(),
}: {
  properties?: Properties;
  now?: Date;
}) {
  const t = useT();
  const language = useAppStore((s) => s.language);
  const purchasedOn = properties?.["purchasedOn"];
  return (
    <DeadlineChips properties={properties} now={now}>
      {purchasedOn?.type === "date" && (
        <Chip
          size="small"
          variant="outlined"
          label={`${datePropertyLabel(t, "purchasedOn")} · ${formatDeadlineDate(purchasedOn.value, language)}`}
        />
      )}
    </DeadlineChips>
  );
}

/**
 * The opt-in trailing column (prototype cell D2): every deadline as a full
 * countdown block, earliest first, two visible then `+N more`; unset rows get a
 * dashed rail and an em dash. Each block keeps its own warning colour.
 */
export function DeadlineColumn({
  properties,
  now = new Date(),
  vocabulary = ITEM_DEADLINE_VOCABULARY,
}: {
  properties?: Properties;
  now?: Date;
  vocabulary?: DeadlineVocabulary;
}) {
  const t = useT();
  const language = useAppStore((s) => s.language);
  const entries = deadlinesOf(properties, vocabulary);

  if (entries.length === 0) {
    return (
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
        <Box
          data-testid="deadline-empty-rail"
          sx={{
            width: 4,
            alignSelf: "stretch",
            minHeight: 32,
            borderRadius: 1,
            border: "1px dashed",
            borderColor: "divider",
          }}
        />
        <Typography variant="caption" color="text.disabled">
          —
        </Typography>
      </Stack>
    );
  }

  const shown = entries.slice(0, 2);
  const hidden = entries.length - shown.length;
  const railStatus = statusOf(shown[0]!.key, shown[0]!.date, vocabulary, now);
  return (
    <Stack direction="row" spacing={1} sx={{ minWidth: 0 }}>
      <Box
        sx={{
          width: 4,
          alignSelf: "stretch",
          minHeight: 32,
          borderRadius: 1,
          bgcolor: deadlineToken(railStatus),
        }}
      />
      <Box sx={{ minWidth: 0 }}>
        {shown.map(({ key, date }) => {
          const status = statusOf(key, date, vocabulary, now);
          return (
            <Box key={key} sx={{ mb: 0.5, "&:last-of-type": { mb: 0 } }}>
              <Typography
                variant="subtitle2"
                data-status={status}
                sx={{ lineHeight: 1.15 }}
                color={status === "later" ? "text.primary" : deadlineToken(status)}
              >
                {countdownLabel(date, t, now)}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {datePropertyLabel(t, key)} · {formatDeadlineDate(date, language)}
              </Typography>
            </Box>
          );
        })}
        {hidden > 0 && (
          <Typography variant="caption" color="text.secondary">
            {t("deadlineMore", { count: String(hidden) })}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

/**
 * Header row above the opt-in column. The deadline label itself is the sort
 * control: click toggles soonest-first and back to name order (prototype C2).
 */
export function DeadlineColumnHeader({
  soonest,
  onToggleSort,
  label,
}: {
  soonest: boolean;
  onToggleSort: () => void;
  label: string;
}) {
  const t = useT();
  return (
    <ButtonBase
      onClick={onToggleSort}
      aria-label={t(soonest ? "deadlineSortByName" : "deadlineSortBySoonest")}
      sx={{
        display: "flex",
        alignItems: "center",
        width: "100%",
        px: 2,
        py: 0.25,
        bgcolor: "action.hover",
        borderRadius: 1,
        color: soonest ? "primary.main" : "text.secondary",
      }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, textAlign: "left" }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ ml: 1, fontWeight: soonest ? 600 : 400 }}>
        {t("deadlineColumn")}
      </Typography>
      {soonest ? <ChevronUpIcon fontSize="inherit" /> : <ChevronDownIcon fontSize="inherit" />}
    </ButtonBase>
  );
}

/** Live urgency counts shown beside the toolbar switch while the column is off. */
export function DeadlineViewControls({
  overdue,
  soon,
  checked,
  onChange,
}: {
  overdue: number;
  soon: number;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const t = useT();
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: "wrap" }}>
      {overdue === 0 && soon === 0 ? (
        <Typography variant="caption" color="text.disabled">
          {t("deadlineNoneDue")}
        </Typography>
      ) : (
        <>
          {overdue > 0 && (
            <Chip
              size="small"
              color="error"
              label={t("deadlineOverdueCount", { count: String(overdue) })}
            />
          )}
          {soon > 0 && (
            <Chip
              size="small"
              color="warning"
              label={t("deadlineSoonCount", { count: String(soon) })}
            />
          )}
        </>
      )}
      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
          />
        }
        label={<Typography variant="body2">{t("deadlines")}</Typography>}
        sx={{ m: 0 }}
      />
    </Stack>
  );
}

/**
 * Overdue / Expiring-soon toggles for the opt-in deadline view. Multi-select:
 * both on shows the union; none on clears the filter. Labels reuse the deadline
 * vocabulary strings, so no new copy for the two controls.
 */
export function DeadlineFilterControls({
  value,
  onChange,
}: {
  value: readonly DeadlineFilter[];
  onChange: (next: DeadlineFilter[]) => void;
}) {
  const t = useT();
  return (
    <ToggleButtonGroup
      size="small"
      value={[...value]}
      onChange={(_event, next: DeadlineFilter[]) => onChange(next)}
      aria-label={t("deadlineFilters")}
    >
      <ToggleButton value="overdue">{t("deadlineOverdue")}</ToggleButton>
      <ToggleButton value="soon">{t("deadlineSoon")}</ToggleButton>
    </ToggleButtonGroup>
  );
}

/**
 * Client-side Overdue / Expiring-soon filter over rows, using the same shared
 * predicate as the server. Returns a new array; no filters means all rows.
 * Filtering only removes rows — warning colours stay derived from the dates.
 */
export function filterByDeadline<T extends { properties?: Properties }>(
  rows: readonly T[],
  filters: readonly DeadlineFilter[],
  vocabulary: DeadlineVocabulary = ITEM_DEADLINE_VOCABULARY,
  now: Date = new Date(),
): T[] {
  if (filters.length === 0) return [...rows];
  return rows.filter((row) => matchesDeadlineFilters(row.properties, filters, vocabulary, now));
}
